import { Input } from '../core/Input';
import { LEVELS } from '../data/levels';
import { MobileControls } from '../mobile/MobileControls';
import { PixelRenderer } from '../render/PixelRenderer';
import { getAssets } from '../render/AssetLoader';
import { SoundManager } from '../render/SoundManager';
import { isSolidChar } from '../render/TileDrawing';
import type { LevelDefinition, Point, RuntimeLevel, RuntimeObjective } from '../types';
import { Hud } from '../ui/Hud';
import { saveProgress, loadProgress, clearProgress, type SaveData } from './SaveManager';

type Phase = 'objectives' | 'exit' | 'complete';

interface Player {
  position: Point;
  facing: Point;
}

export class PixelLoveGame {
  private readonly root: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly input: Input;
  private readonly mobileControls: MobileControls;
  private readonly renderer: PixelRenderer;
  private readonly hud: Hud;
  private readonly customLevels: LevelDefinition[] | null;
  private readonly sound: SoundManager;

  private levelIndex = 0;
  private level: RuntimeLevel;
  private player: Player = {
    position: { x: 0, y: 0 },
    facing: { x: 1, y: 0 },
  };
  private phase: Phase = 'objectives';
  private activeObjectiveIndex = 0;
  private holdProgress = 0;
  private score = 0;
  private combo = 1;
  private lastActionTime = -1000;
  private levelStartSeconds = 0;
  private lastFrameSeconds = 0;
  private objectiveLabel = '';
  private readonly playerRadius = 0.38;
  private initialized = false;
  private interactionBubbleVisible = false;
  private interactionBubbleText = '';
  private interactionBubbleFullText = '';
  private interactionBubbleAnchor: Point | null = null;
  private interactionBubbleChars = 0;
  private interactionBubbleCarry = 0;
  private readonly interactionCharsPerSecond = 44;
  private lastSaveSeconds = 0;
  private restoredElapsed = 0;

  constructor(root: HTMLElement, customLevels?: LevelDefinition[]) {
    this.root = root;
    this.customLevels = customLevels ?? null;
    this.sound = new SoundManager(getAssets()?.sounds ?? new Map(), 0.4);
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'game-canvas';
    this.root.appendChild(this.canvas);

    const params = new URLSearchParams(window.location.search);
    const forceMobileControls = params.get('mobile') === '1';

    this.input = new Input();
    this.mobileControls = new MobileControls(this.root, forceMobileControls);
    this.renderer = new PixelRenderer(this.canvas);
    this.hud = new Hud(this.root);

    const requestedLevel = Number.parseInt(params.get('level') ?? '', 10);
    const levels = this.getLevels();
    if (Number.isFinite(requestedLevel) && requestedLevel >= 1 && requestedLevel <= levels.length) {
      this.levelIndex = requestedLevel - 1;
    }

    this.level = this.createRuntimeLevel(levels[this.levelIndex]);
    this.loadLevel(this.levelIndex, 0, true);
    this.restoreSavedProgress();
  }

  public resize(_width: number, _height: number): void {
    this.renderer.resize();
  }

  public update(timeMs: number): void {
    const nowSeconds = timeMs * 0.001;
    if (!this.initialized) {
      this.lastFrameSeconds = nowSeconds;
      this.levelStartSeconds = nowSeconds;
      this.initialized = true;
    }

    const delta = Math.min(0.05, Math.max(0, nowSeconds - this.lastFrameSeconds));
    this.lastFrameSeconds = nowSeconds;

    this.updateMovement(delta);
    this.updateInteractions(nowSeconds, delta);
    this.autoSave(nowSeconds);
    this.renderHud(nowSeconds);

    // Update HTML message bubble
    if (this.interactionBubbleVisible && this.interactionBubbleText) {
      this.hud.showMessage(this.interactionBubbleText);
    } else {
      this.hud.hideMessage();
    }

    this.renderer.render({
      level: this.level,
      player: this.player.position,
      playerFacing: this.player.facing,
      currentTarget: this.getCurrentTargetPosition(),
      objectives: this.level.objectives,
      phase: this.phase,
      activeObjectiveIndex: this.activeObjectiveIndex,
      holdProgress: this.holdProgress,
      elapsedSeconds: this.getLevelElapsedSeconds(nowSeconds),
    });

    this.input.endFrame();
  }

  public dispose(): void {
    this.hud.dispose();
    this.input.dispose();
    this.mobileControls.dispose();
    this.canvas.remove();
  }

  private updateMovement(delta: number): void {
    const mobileAxis = this.mobileControls.getMovementAxis();
    const moveX = this.clamp((this.input.isDown('right') ? 1 : 0) - (this.input.isDown('left') ? 1 : 0) + mobileAxis.x, -1, 1);
    const moveY = this.clamp((this.input.isDown('down') ? 1 : 0) - (this.input.isDown('up') ? 1 : 0) + mobileAxis.y, -1, 1);

    if (moveX === 0 && moveY === 0) {
      return;
    }

    const length = Math.hypot(moveX, moveY);
    const directionX = moveX / length;
    const directionY = moveY / length;
    this.player.facing.x = directionX;
    this.player.facing.y = directionY;

    const speed = this.input.isDown('run') ? 6 : 4.2;
    const amountX = directionX * speed * delta;
    const amountY = directionY * speed * delta;
    this.movePlayerWithCollision(amountX, amountY);
  }

  private updateInteractions(nowSeconds: number, delta: number): void {
    this.updateInteractionBubble(delta, this.isPlayerInsideBubbleInteractionRange());

    const interactPressed = this.input.wasPressed('interact') || this.mobileControls.consumeInteractPressed();

    // Allow re-reading any already-activated objective when nearby
    if (interactPressed) {
      for (const obj of this.level.objectives) {
        if (!obj.activated) continue;
        const dist = this.distance(this.player.position, this.toCenteredPoint(obj.position));
        if (dist <= 1.0 && this.hasLineOfSight(this.player.position, this.toCenteredPoint(obj.position))) {
          this.startInteractionBubble(obj.title, obj.message, obj.position);
          this.sound.playRandom('coin');
          this.holdProgress = 0;
          return;
        }
      }
    }

    const currentTarget = this.getCurrentTarget();
    if (!currentTarget) {
      this.holdProgress = 0;
      return;
    }

    const targetPosition = this.getTargetPosition(currentTarget);
    const distance = this.distance(this.player.position, targetPosition);
    const canInteract = distance <= 1.0 && this.hasLineOfSight(this.player.position, targetPosition);

    if (canInteract && interactPressed) {
      if (this.phase === 'objectives') {
        this.activateObjective(currentTarget as RuntimeObjective, nowSeconds);
      } else if (this.phase === 'exit') {
        this.activateExit(nowSeconds);
      }
    }

    this.holdProgress = 0;
  }

  private activateObjective(objective: RuntimeObjective, nowSeconds: number): void {
    if (objective.activated) {
      return;
    }

    this.startInteractionBubble(objective.title, objective.message, objective.position);
    objective.activated = true;
    this.sound.playRandom('coin');
    this.awardPoints(objective.points, nowSeconds);
    this.activeObjectiveIndex += 1;
    this.saveNow(nowSeconds);
    if (this.activeObjectiveIndex < this.level.objectives.length) {
      return;
    }

    this.phase = 'exit';
    this.objectiveLabel = 'Activer le portail de sortie';
  }

  private activateExit(nowSeconds: number): void {
    this.sound.play('select-a');
    this.awardPoints(330, nowSeconds);
    const isLastLevel = this.levelIndex >= this.getLevels().length - 1;

    if (!isLastLevel) {
      this.loadLevel(this.levelIndex + 1, nowSeconds, false);
      this.saveNow(nowSeconds);
      return;
    }

    this.startInteractionBubble(this.level.completionTitle, this.level.completionMessage, this.level.exit);
    this.phase = 'complete';
    this.objectiveLabel = 'Promesse scellee';
    clearProgress();
  }

  private awardPoints(basePoints: number, nowSeconds: number): number {
    if (nowSeconds - this.lastActionTime < 8) {
      this.combo += 1;
    } else {
      this.combo = 1;
    }
    this.lastActionTime = nowSeconds;

    const elapsed = this.getLevelElapsedSeconds(nowSeconds);
    const timeBonus = Math.max(0, Math.round((this.level.parSeconds - elapsed) * 4));
    const comboBonus = (this.combo - 1) * 40;
    const total = basePoints + timeBonus + comboBonus;
    this.score += total;
    return total;
  }

  private renderHud(nowSeconds: number): void {
    const elapsed = this.getLevelElapsedSeconds(nowSeconds);
    const progressMax = this.level.objectives.length;
    const progressValue = this.phase === 'exit' || this.phase === 'complete' ? progressMax : this.activeObjectiveIndex;
    const targetInfo = this.getTargetLabel();
    const prompt = this.getPrompt();

    this.hud.render({
      levelName: this.level.name,
      levelIndex: this.level.index,
      totalLevels: this.getLevels().length,
      objectiveLabel: this.objectiveLabel,
      progressLabel: `${progressValue}/${progressMax}`,
      targetLabel: targetInfo,
      score: this.score,
      elapsedSeconds: elapsed,
      parSeconds: this.level.parSeconds,
      combo: this.combo,
      prompt,
    });
  }

  private getPrompt(): string {
    if (this.phase === 'complete') {
      return '\u0627\u0644\u0645\u063a\u0627\u0645\u0631\u0629 \u0633\u0627\u0644\u0627\u062a. F2 \u0644\u0644\u062e\u0631\u064a\u0637\u0629.';
    }

    const target = this.getCurrentTargetPosition();
    if (!target) {
      return '\u0627\u0643\u062a\u0634\u0641.';
    }

    const distance = this.distance(this.player.position, target);
    if (distance <= 1.2) {
      if (this.mobileControls.isActive()) {
        return '\u0627\u0636\u063a\u0637 INTERACT \u0628\u0627\u0634 \u062a\u062a\u0641\u0627\u0639\u0644.';
      }
      return '\u0627\u0636\u063a\u0637 Espace / E \u0628\u0627\u0634 \u062a\u062a\u0641\u0627\u0639\u0644.';
    }

    if (this.mobileControls.isActive()) {
      return '\u062a\u062d\u0631\u0643 \u0628\u0627\u0644\u062c\u0648\u064a\u0633\u062a\u064a\u0643 \u0648\u0627\u0636\u063a\u0637 INTERACT.';
    }
    return '\u0642\u0631\u0628 \u0645\u0646 \u0627\u0644\u0647\u062f\u0641 \u0628\u0627\u0634 \u062a\u062a\u0641\u0627\u0639\u0644.';
  }

  private getTargetLabel(): string {
    if (this.phase === 'complete') {
      return '\u0627\u0644\u0648\u0639\u062f \u062a\u062d\u0642\u0642';
    }

    if (this.phase === 'exit') {
      const distance = this.distance(this.player.position, this.level.exit);
      return `\u0627\u0644\u0628\u0648\u0627\u0628\u0629 \u0627\u0644\u0623\u062e\u064a\u0631\u0629 - ${distance.toFixed(1)}m`;
    }

    const objective = this.level.objectives[this.activeObjectiveIndex];
    if (!objective) {
      return 'N/A';
    }
    const distance = this.distance(this.player.position, objective.position);
    return `${objective.label} - ${distance.toFixed(1)}m`;
  }

  private getCurrentTarget(): RuntimeObjective | Point | null {
    if (this.phase === 'complete') {
      return null;
    }

    if (this.phase === 'exit') {
      return this.level.exit;
    }

    return this.level.objectives[this.activeObjectiveIndex] ?? null;
  }

  private getCurrentTargetPosition(): Point | null {
    const target = this.getCurrentTarget();
    if (!target) {
      return null;
    }
    return this.getTargetPosition(target);
  }

  private getTargetPosition(target: RuntimeObjective | Point): Point {
    if ('position' in target) {
      return target.position;
    }
    return target;
  }

  private movePlayerWithCollision(dx: number, dy: number): void {
    const maxAxis = Math.max(Math.abs(dx), Math.abs(dy));
    const steps = Math.max(1, Math.ceil(maxAxis / 0.08));
    const stepX = dx / steps;
    const stepY = dy / steps;

    for (let i = 0; i < steps; i += 1) {
      this.player.position.x += stepX;
      this.resolveWallCollisions();
      this.player.position.y += stepY;
      this.resolveWallCollisions();
    }
  }

  private resolveWallCollisions(): void {
    const minTileX = Math.floor(this.player.position.x - this.playerRadius) - 1;
    const maxTileX = Math.floor(this.player.position.x + this.playerRadius) + 1;
    const minTileY = Math.floor(this.player.position.y - this.playerRadius) - 1;
    const maxTileY = Math.floor(this.player.position.y + this.playerRadius) + 1;

    for (let tileY = minTileY; tileY <= maxTileY; tileY += 1) {
      for (let tileX = minTileX; tileX <= maxTileX; tileX += 1) {
        if (!this.isSolidTile(tileX, tileY)) {
          continue;
        }

        const minX = tileX;
        const maxX = tileX + 1;
        const minY = tileY;
        const maxY = tileY + 1;

        const nearestX = this.clamp(this.player.position.x, minX, maxX);
        const nearestY = this.clamp(this.player.position.y, minY, maxY);
        let pushX = this.player.position.x - nearestX;
        let pushY = this.player.position.y - nearestY;
        let distSq = pushX * pushX + pushY * pushY;

        if (distSq >= this.playerRadius * this.playerRadius) {
          continue;
        }

        if (distSq < 0.000001) {
          const centerX = tileX + 0.5;
          const centerY = tileY + 0.5;
          const deltaX = this.player.position.x - centerX;
          const deltaY = this.player.position.y - centerY;
          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            pushX = deltaX >= 0 ? 1 : -1;
            pushY = 0;
          } else {
            pushX = 0;
            pushY = deltaY >= 0 ? 1 : -1;
          }
          distSq = 1;
        }

        const dist = Math.sqrt(distSq);
        const overlap = this.playerRadius - dist;
        this.player.position.x += (pushX / dist) * overlap;
        this.player.position.y += (pushY / dist) * overlap;
      }
    }
  }

  private isSolidAt(x: number, y: number): boolean {
    return this.isSolidTile(Math.floor(x), Math.floor(y));
  }

  private isSolidTile(tileX: number, tileY: number): boolean {
    if (tileX < 0 || tileX >= this.level.width || tileY < 0 || tileY >= this.level.height) {
      return true;
    }
    const tile = this.level.tiles[tileY][tileX];
    return isSolidChar(tile);
  }

  private hasLineOfSight(from: Point, to: Point): boolean {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.hypot(dx, dy);
    if (distance <= 0.0001) {
      return true;
    }

    const samples = Math.max(6, Math.ceil(distance * 8));
    for (let i = 1; i < samples; i += 1) {
      const t = i / samples;
      const x = from.x + dx * t;
      const y = from.y + dy * t;
      if (this.isSolidAt(x, y)) {
        return false;
      }
    }

    return true;
  }

  private getLevelElapsedSeconds(nowSeconds: number): number {
    return Math.max(0, Math.floor(nowSeconds - this.levelStartSeconds) + this.restoredElapsed);
  }

  private loadLevel(index: number, nowSeconds: number, firstLoad: boolean): void {
    this.levelIndex = index;
    this.level = this.createRuntimeLevel(this.getLevels()[index]);
    this.player.position = { ...this.level.spawn };
    this.player.facing = { x: 1, y: 0 };
    this.phase = 'objectives';
    this.activeObjectiveIndex = 0;
    this.holdProgress = 0;
    this.combo = 1;
    this.lastActionTime = -1000;
    this.levelStartSeconds = nowSeconds;
    this.restoredElapsed = 0;
    this.objectiveLabel = this.level.objectiveLabel;

    this.hideInteractionBubble();

    if (!firstLoad) {
      return;
    }
  }

  private createRuntimeLevel(definition: LevelDefinition): RuntimeLevel {
    const width = definition.map[0]?.length ?? 0;
    const height = definition.map.length;
    const tiles = definition.map.map((row) => row.split(''));
    const rotations = definition.rotations
      ? definition.rotations.map((row) => [...row])
      : definition.map.map((row) => new Array(row.length).fill(0));
    const spawn = this.toCenteredPoint(definition.spawn);
    const exit = this.toCenteredPoint(definition.exit);
    const objectives = definition.objectives.map((objective) => ({
      ...objective,
      position: this.toCenteredPoint(objective.position),
      activated: false,
    }));

    return {
      ...definition,
      spawn,
      exit,
      width,
      height,
      tiles,
      rotations,
      objectives,
    };
  }

  private distance(a: Point, b: Point): number {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  private toCenteredPoint(point: Point): Point {
    return {
      x: point.x + 0.5,
      y: point.y + 0.5,
    };
  }

  private startInteractionBubble(title: string, message: string, anchor: Point): void {
    const normalizedTitle = title.trim();
    const normalizedMessage = message.trim();
    const fullText = normalizedTitle ? `${normalizedTitle} - ${normalizedMessage}` : normalizedMessage;
    if (!fullText) {
      this.hideInteractionBubble();
      return;
    }

    this.interactionBubbleVisible = true;
    this.interactionBubbleText = '';
    this.interactionBubbleFullText = fullText;
    this.interactionBubbleAnchor = { ...anchor };
    this.interactionBubbleChars = 0;
    this.interactionBubbleCarry = 0;
  }

  private updateInteractionBubble(delta: number, keepVisible: boolean): void {
    if (!this.interactionBubbleVisible || !this.interactionBubbleFullText || !this.interactionBubbleAnchor) {
      this.hideInteractionBubble();
      return;
    }

    if (!keepVisible) {
      this.hideInteractionBubble();
      return;
    }

    this.advanceInteractionBubbleTyping(delta);
  }

  private advanceInteractionBubbleTyping(delta: number): void {
    if (!this.interactionBubbleFullText) {
      this.interactionBubbleText = '';
      return;
    }

    this.interactionBubbleCarry += delta * this.interactionCharsPerSecond;
    const charsToAdd = Math.floor(this.interactionBubbleCarry);
    if (charsToAdd > 0) {
      this.interactionBubbleChars = Math.min(
        this.interactionBubbleFullText.length,
        this.interactionBubbleChars + charsToAdd
      );
      this.interactionBubbleCarry -= charsToAdd;
    }

    this.interactionBubbleText = this.interactionBubbleFullText.slice(0, this.interactionBubbleChars);
  }

  private getLevels(): LevelDefinition[] {
    return this.customLevels ?? LEVELS;
  }

  private hideInteractionBubble(): void {
    this.interactionBubbleVisible = false;
    this.interactionBubbleText = '';
    this.interactionBubbleFullText = '';
    this.interactionBubbleAnchor = null;
    this.interactionBubbleChars = 0;
    this.interactionBubbleCarry = 0;
  }

  private isPlayerInsideBubbleInteractionRange(): boolean {
    if (!this.interactionBubbleAnchor) {
      return false;
    }
    const distance = this.distance(this.player.position, this.interactionBubbleAnchor);
    return distance <= 1.05 && this.hasLineOfSight(this.player.position, this.interactionBubbleAnchor);
  }

  /* ── Auto-save / restore ── */

  private autoSave(nowSeconds: number): void {
    if (nowSeconds - this.lastSaveSeconds < 2) return;
    this.lastSaveSeconds = nowSeconds;
    this.saveNow(nowSeconds);
  }

  private saveNow(nowSeconds: number): void {
    if (this.phase === 'complete') return;
    if (this.customLevels) return;  // don't save editor preview sessions
    const data: SaveData = {
      version: 1,
      levelIndex: this.levelIndex,
      score: this.score,
      combo: this.combo,
      phase: this.phase,
      activeObjectiveIndex: this.activeObjectiveIndex,
      playerPosition: { x: this.player.position.x, y: this.player.position.y },
      playerFacing: { x: this.player.facing.x, y: this.player.facing.y },
      activatedObjectiveIds: this.level.objectives
        .filter(o => o.activated)
        .map(o => o.id),
      levelElapsed: this.getLevelElapsedSeconds(nowSeconds),
    };
    saveProgress(data);
  }

  private restoreSavedProgress(): void {
    if (this.customLevels) return;
    const save = loadProgress();
    if (!save) return;

    const levels = this.getLevels();
    if (save.levelIndex < 0 || save.levelIndex >= levels.length) return;

    // Reload the saved level
    this.levelIndex = save.levelIndex;
    this.level = this.createRuntimeLevel(levels[this.levelIndex]);
    this.score = save.score;
    this.combo = save.combo;
    this.phase = save.phase;
    this.activeObjectiveIndex = save.activeObjectiveIndex;
    this.player.position = { ...save.playerPosition };
    this.player.facing = { ...save.playerFacing };
    this.restoredElapsed = save.levelElapsed;
    this.objectiveLabel = this.phase === 'exit'
      ? 'Activer le portail de sortie'
      : this.level.objectiveLabel;

    // Restore activated objectives
    const activatedSet = new Set(save.activatedObjectiveIds);
    for (const obj of this.level.objectives) {
      if (activatedSet.has(obj.id)) obj.activated = true;
    }
  }
}
